import { UserVO } from "@/types/REST-API-DOMAIN/UserVO";
import { api } from "@/helpers/ApiClient";
import { NewSubscriptionVO } from "@/types/REST-API-DOMAIN/NewSubscriptionVO";
import { NewTeamVO } from "@/types/REST-API-DOMAIN/NewTeamVO";
import moment from "moment";
import { NewNoteVO } from "@/types/REST-API-DOMAIN/NewNoteVO";
import { CreateDMDTO } from "@/types/REST-API-DOMAIN/CreateDMDTO";
import { MyPlayersByTeam } from "@/types";

// Get currently logged in user
export async function getUser(): Promise<UserVO> {
  const userRequest = await api.get("/rest-api/users/profile");
  const resp = await userRequest.data;
  return resp;
}

export async function getJiraToken() {
  const request = await api.get("/rest-api/jira-chat/token");
  return request.data;
}

export async function getAlerts(): Promise<UserVO> {
  const alertRequest = await api.get(
    "/rest-api/users/pending-alerts?os=web&version=2.0"
  );
  const resp = await alertRequest.data;
  return resp;
}

// Get currently logged in user
export async function getPublicEvent(roomId: string) {
  try {
    const userRequest = await api.get(
      `/rest-api/event/connect/public/${roomId}`
    );
    return await userRequest.data;
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getUserWithPlayers(userId: string) {
  try {
    return await api
      .get(`/rest-api/event/connect/userwithplayers/${userId}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function createNewSubscription(
  newSubscriptionVO: NewSubscriptionVO
) {
  return api.post(
    "/rest-api/payments/create/org/subscription",
    newSubscriptionVO
  );
}

export async function createNewTeam(newTeamVO: NewTeamVO) {
  return api.post("/rest-api/organization/add/team", newTeamVO);
}

export async function getPromotionsByCode(code: string) {
  return await api.get(`/rest-api/payments/promotions/${code}`);
}

export async function getConnectAndStoragePrices() {
  return await api.get(`/rest-api/payments/connect/storage/prices`);
}

export async function getPrice(price: string) {
  return await api.get(`/rest-api/payments/price/${price}`);
}

export async function getProduct(product: string) {
  return await api.get(`/rest-api/payments/product/${product}`);
}

export async function getTeamsForExistingOrg(id: string) {
  return await api.get(`/rest-api/payments/teams/${id}`);
}

export async function getStripePKey() {
  const request = await api.get(`/rest-api/payments/stripe/pkey`);
  return request.data;
}

export async function getProductsAndPricesObject() {
  const request = await api.get(`/rest-api/payments/products/prices/object`);
  return request.data;
}

export async function getUpcomingInvoice(orgId: string) {
  const request = await api.get(
    `/rest-api/payments/upcoming/invoices/${orgId}`
  );
  return request.data;
}

export async function getLicensesRemaining(orgId: string) {
  const request = await api.get(
    `/rest-api/payments/licenses/remaining/${orgId}`
  );
  return request.data;
}

export async function getOrgsWhereUserHasAddTeamPermission() {
  const request = await api.get(`/rest-api/organization/user/canAddTeam`);
  return request.data;
}

export async function fetchActivePaymentProviders(organizationId: string) {
  const request = await api.get(
    "/rest-api/fee/payment-accounts/" + organizationId
  );
  return request.data;
}

export async function fetchAllPaymentProviders(organizationId: string) {
  const request = await api.get(
    "/rest-api/fee/payment-accounts/" + organizationId + "/all"
  );
  return request.data;
}

export async function togglePaymentProviderActive(providerId: string) {
  const request = await api.get(
    "/rest-api/fee/payment-accounts/toggle/" + providerId
  );
  return request.data;
}

export async function changePaymentProviderFees(
  providerId: string,
  paymentAccountVO: any
) {
  const request = await api.post(
    "/rest-api/fee/payment-accounts/update",
    paymentAccountVO
  );
  return request.data;
}

export async function getAddedTeamsInvoice(
  orgId: string,
  licensesToAdd: number
) {
  const request = await api.get(
    `/rest-api/payments/estimate/addedTeams/${orgId}/${licensesToAdd}`
  );
  return request.data;
}

export async function getSubscriptionItemByOrgId(orgId: string) {
  const request = await api.get(
    `/rest-api/payments/subscription/byOrgId/${orgId}`
  );
  return request.data;
}

export async function purchaseLicense(
  orgId: string,
  subscriptionId: string,
  quantity: number | null
) {
  const request = await api.get(
    `/rest-api/payments/purchase/team/license/${orgId}?subscriptionId=${subscriptionId}&quantity=${quantity}`
  );
  return request.data;
}

export async function getBillingInfoByOrgId(orgId: string) {
  const request = await api.get(`/rest-api/organization/billing/${orgId}`);
  return request.data;
}

export async function getCreateTeamSubscriptionInfo(
  orgId: string,
  subscriptionId: string | null,
  licensesToAdd: number | null
) {
  const request = await api.get(
    `/rest-api/payments/createteam/subscriptioninfo/${orgId}?licensesToAdd=${licensesToAdd}&subscriptionId=${subscriptionId}`
  );
  return request.data;
}

export async function getUserById(id: string) {
  try {
    const userRequest = await fetch(`/rest-api/users/${id}`);
    return await userRequest.json();
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getConnectEvent(roomId: any, userId: any) {
  try {
    return await api
      .get(`/rest-api/event/connect/${roomId}/${userId}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

// Get Event Row Data from DB
export async function getEventDetails(roomId: string) {
  try {
    return await api.get(`/rest-api/event/details/${roomId}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

// Get Org Unit By Id
export async function getOrgUnit(orgId: string) {
  try {
    return await api.get(`/rest-api/orgUnit/byId/${orgId}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getOrgUnitByPlayerId(id: string) {
  try {
    return await api
      .get(`/rest-api/orgUnit/byPlayerId/${id}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

// Get Array of Player Entries of players invited to invite
export async function getPlayerEvent(roomId: string) {
  try {
    return await api.get(`/rest-api/event/players/${roomId}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

// Get Array of Player Entries of players invited to invite
export async function getInvitedUsers(roomId: string) {
  try {
    return await api.get(`/rest-api/event/users/${roomId}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

// Get the user entry of the user who created the event
export async function getEventCreator(roomId: string) {
  try {
    return await api.get(`/rest-api/event/creator/${roomId}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

//get associated Player ID
export async function getInvitedPlayers(userId: string, eventId: string) {
  try {
    return await api
      .get(`/rest-api/event/invited/${eventId}/${userId}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get emails sent to currently logged in user
 */
export async function getEmailsSentToMe() {
  try {
    return api.get(`/rest-api/mail/getEmails`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get recipients for a team email
 */
export async function getEmailRecipients(emailId: string) {
  try {
    return api
      .get(`/rest-api/mail/team/email-recipients/${emailId}`)
      .then((response: any) => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get emails sent to currently logged in user
 */
export async function getEmailsFromMandrill() {
  try {
    return api.get(`/api/v1/emails`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getEmailsByOrgUnitId(id: string) {
  try {
    return api.get(`/rest-api/mail/team/getEmails/${id}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get email for the id specified, sent to currently logged in user
 */
export async function getEmail(id: string) {
  try {
    return api.get(`/api/v1/email/` + id).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get emails sent to currently logged in user
 */
export async function mandrillSearch(
  to: string,
  from: string,
  subject: string,
  daysBack: number
) {
  try {
    return api
      .get(
        `/api/v1/mandrillSearch?to=${to || ""}&from=${from ||
          ""}&subject=${subject || ""}&daysBack=${daysBack || 7}`
      )
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function markOpen(id: string) {
  return api.get(`/rest-api/mail/mark-open/${id}`);
}

/**
 * get today's information for current user
 */
export async function getToday() {
  try {
    const startUtc = moment().startOf("d"); // today at midnight, UTC
    const url = `/api/v1/today?startUtc=` + startUtc.toISOString();
    return api.get(url).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getUnreadMessageCount() {
  try {
    return api.get(`/rest-api/chat/unread-count`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team (players) data for current user
 */
export async function getTeams() {
  try {
    return api.get(`/api/v1/teams`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team data for the specified id
 */
export async function getTeam(id: string, includeOrg = true) {
  try {
    return api
      .get(`/api/v1/team/${id}?includeOrg=${includeOrg}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get overview data for the specified id
 */
export async function getOrgOverview(id: string) {
  try {
    return api.get(`/api/v1/orgOverview/${id}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team data for the specified id
 */
export async function getNotInvitedPlayers(eventId: string) {
  return api.get(`/api/v1/notInvitedPlayers/${eventId}`);
}

export async function getVenueErrors(id: string) {
  try {
    return api.get(`/rest-api/orgUnit/venueErrors/${id}`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team events data for the given team id, month, and year
 */
export async function getTeamEventsByMonth(
  id: string,
  month: string,
  year: string
) {
  try {
    const start = moment(`${year}-${month}-01`);
    const end = start.clone().add(1, "months");
    // const end = start.clone().endOf("month");

    return api
      .get(
        `/api/v1/teamEventsByMonth/${id}?start=${start.format(
          "YYYY-MM-DD"
        )}&end=${end.format("YYYY-MM-DD")}&millis=true`
      )
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team (players) data for current user
 */
export async function getPlayersAndTeams() {
  try {
    return api.get(`/api/v1/playersAndTeams`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get player events for current user's players
 */
export async function getPlayerEvents() {
  try {
    return api.get(`/api/v1/playerEvents`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 * get team (players) data for current user, grouped by team
 */
export async function getPlayersByTeam(): Promise<MyPlayersByTeam[] | null> {
  try {
    return api.get(`/api/v1/playersByTeam`).then(response => {
      return response.data;
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 *
 * @param playerInviteId
 * @param inviteStatus
 * @param note
 * @returns {Promise<null|apiResponse<any>>}
 */
export async function invitePlayerToEvent(
  eventId: string,
  playerId: string,
  inviteStatus: string
) {
  try {
    return api
      .post(
        `/event/addPlayerInvite?eventId=${eventId}&playerId=${playerId}&inviteStatus=${inviteStatus}`
      )
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function inviteAllPlayersToEvent(
  eventId: string,
  inviteStatus: string
) {
  try {
    return api
      .post(
        `/event/addPlayerInviteForAllUninvitedPlayers?eventId=${eventId}&inviteStatus=${inviteStatus}`
      )
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 *
 * @param eventId
 * @param playerInviteId
 * @returns {Promise<null|apiResponse<any>>}
 */
export async function removePlayerFromEvent(playerInviteId: string) {
  try {
    return api
      .post(`/orgUnit/removePlayerInvite/${playerInviteId}`)
      .then(response => {
        return response.data;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

/**
 *
 * @param playerInviteId
 * @param inviteStatus
 * @param note
 * @returns {Promise<null|apiResponse<any>>}
 */
export async function updatePlayerAvailability(
  playerInviteId: string,
  inviteStatus: string,
  note: string
) {
  try {
    return api
      .get(`/user/updateInviteStatus/` + playerInviteId + `.json`, {
        params: {
          format: "js",
          status: inviteStatus,
          note: note
        }
      })
      .then(response => {
        return response;
      });
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function cloneEvent(orgId: string, body: any) {
  return api.post(`/api/v1/cloneEvent/${orgId}`, body, {
    headers: { "Content-Type": "multipart/form-data" }
  });
}
export async function cloneEventToTeams(orgId: string, body: any) {
  return api.post(`/apiV1/cloneEventToTeams/`, body, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });
}

// export async function inviteNewFan(player: any, email: string) {
//     return api.post(`/api/v1/inviteUser`, {
//         playerId: player,
//         email: `${email}`,
//         inviteType: "Fan"
//     });
// }

export async function deleteEvent(eventId: string) {
  const request = await api.get(`/api/v1/deleteEvent/${eventId}`);
  return request;
}

export async function createNewNote(note: NewNoteVO) {
  return api.post("/rest-api/player/add/note", note);
}

export async function deleteNote(id: number) {
  return api.get(`/rest-api/player/delete/note/${id}`);
}

export async function getNotesForPlayer(playerId: string) {
  return api.get(`/rest-api/player/notes/${playerId}`);
}

export async function getWaiversForPlayer(playerId: string) {
  return api.get(`/rest-api/player/waivers/${playerId}`);
}

export async function inviteNewFan(player: any, email: string) {
  const body = new FormData();
  body.append("playerId", player.id);
  body.append("email", email);
  body.append("inviteType", "Fan");
  return api.post(`/api/v1/inviteUser`, body);
}

export async function inviteNewFollower(player: any, email: string) {
  const body = new FormData();
  body.append("playerId", player.id);
  body.append("email", email);
  body.append("inviteType", "Follower");
  return api.post(`/api/v1/inviteUser`, body);
}

export async function requestFanAccess(coachEmail: string, playerName: string) {
  const body = new FormData();
  body.append("email", coachEmail);
  body.append("playerName", playerName);
  return api.post(`/api/v1/requestInvite`, body);
}

export async function requestCoachAccess(coachEmail: string, teamName: string) {
  const body = {
    email: coachEmail,
    teamName: teamName
  };

  return api.post(`/api/v1/requestAccessToTeam`, body);
}

export async function removeUserInvite(player: any, email: string) {
  const body = new FormData();
  body.append("email", email);
  return api.post(`/player/removeUserInvite/${player.id}`, body);
}

export async function removePlayer(
  teamId: string,
  playerId: string,
  removeFromEvents: boolean
) {
  const body = new FormData();
  body.append("teamId", teamId);
  body.append("playerId", playerId);
  body.append("removeFromEvents", removeFromEvents.toString());
  return api.post(`/api/v1/removePlayer`, body, {
    headers: { "Content-Type": "multipart/form-data" }
  });
}

export async function addPlayersToTeam(
  teamId: string,
  playerIds: Array<string>,
  addToUpcomingEvents: boolean,
  defaultStatus: string
) {
  const body = new FormData();
  body.append("orgUnitId", teamId);
  body.append("playersToAdd", JSON.stringify(playerIds));
  body.append("addToUpcomingEvents", addToUpcomingEvents ? "on" : "off");
  body.append("inviteStatus", defaultStatus);
  return api.post(`/api/v1/addPlayersToTeam`, body, {
    headers: { "Content-Type": "multipart/form-data" }
  });
}

export async function getPlayerReportByOrganization(organizationId: string) {
  return api.get(`/api/v1/playerReportByOrganization/${organizationId}`);
}

export async function getPlayerReportByTeam(teamId: string) {
  return api.get(`/api/v1/playerReportByTeam/${teamId}`);
}

export async function getUsersPlayers() {
  return api.get(`/rest-api/users/players`);
}

export async function getFullUser() {
  return api.get(`/rest-api/users/full`);
}

export async function archiveTeam(id: string) {
  return api.post(`/o/${id}/archive`);
}

export async function unarchiveTeam(id: string) {
  return api.post(`/o/${id}/unarchive`);
}

export async function getAvailableLicenses(orgId: string) {
  return api.get(`/rest-api/orgUnit/availableLicenses/${orgId}`);
}

export async function getAvailableEmailRecipients(ids: Array<string>) {
  return api.post(`/rest-api/mail/available-recipients`, ids);
}
export async function getAvailableParticipants(organizationId: string) {
  const url = `/rest-api/chat-user/available-participants?organizationId=${organizationId}`;
  return api.get(url).then(resp => {
    if (resp.status === 200 && resp.data.status === "success") {
      return resp.data.data.chatParticipants;
    } else {
      throw Error(
        `ERROR getAvailableParticipants: ${resp.status} - ${resp.statusText}`
      );
    }
  });
}
export async function getAvailableParticipantsByTeam(
  organizationId: string,
  teamIds: string[]
) {
  const teamIdsParam = teamIds.join();
  const url = `/rest-api/chat-user/available-participants-by-team?organizationId=${organizationId}&teamIds=${teamIdsParam}`;
  return api.get(url).then(resp => {
    if (resp.status === 200 && resp.data.status === "success") {
      return resp.data.data.chatParticipants;
    } else {
      throw Error(
        `ERROR getAvailableParticipantsByTeam: ${resp.status} - ${resp.statusText}`
      );
    }
  });
}
export async function getAvailableParticipantsForPlayers(
  organizationId: string,
  playerIds: string[]
) {
  const playerIdsParam = playerIds.join();
  const url = `/rest-api/chat-user/available-participants-for-players?organizationId=${organizationId}&playerIds=${playerIdsParam}`;
  return api.get(url).then(resp => {
    if (resp.status === 200 && resp.data.status === "success") {
      return resp.data.data.chatParticipants;
    } else {
      throw Error(
        `ERROR getAvailableParticipantsByTeam: ${resp.status} - ${resp.statusText}`
      );
    }
  });
}

export async function createDm(dto: CreateDMDTO) {
  return api.post("/rest-api/chat/create/dm", dto);
}

export async function updateChannelFriendlyName(
  channelId: string,
  friendlyName: string
) {
  return api.post(
    `/rest-api/chat/updateFriendlyName/${channelId}/${friendlyName}`
  );
}

export async function isChatEnabled(orgId: string) {
  return api.get(`/rest-api/chat/enabled/${orgId}`);
}

export async function getOrganizationsUserHasAccessToChat() {
  return api.get("/rest-api/chat-user/available-orgs").then(resp => {
    resp.data.sort((a: any, b: any) =>
      a.orgName > b.orgName ? 1 : b.orgName > a.orgName ? -1 : 0
    );
    return resp;
  });
}

export async function getPlayersFans(playerId: string) {
  return api.get(`/rest-api/player/fans/${playerId}`);
}

export async function getOrgsDetails() {
  const request = await api.get(`/rest-api/organization/details`);
  return request.data;
}

export async function getSubscriptionDataForSales(id: string) {
  const request = await api.get(`/rest-api/organization/details/${id}`);
  return request.data;
}

export function react(message: any, reaction: any) {
  const url =
    "/rest-api/chat/react-to-message/" +
    message.conversation.sid +
    "/" +
    message.sid +
    "/" +
    reaction;

  return api.get(url);
}

export async function userArchiveOrgUnit(orgUnitId: string) {
  const url = `/rest-api/users/archive/${orgUnitId}`;
  const res = await api.post(url);
  return res;
}

export async function userUnarchiveOrgUnit(orgUnitId: string) {
  const url = `/rest-api/users/unarchive/${orgUnitId}`;
  const res = await api.post(url);
  return res;
}

export async function loadSigningKey() {
  return api.get("/api/v1/awsSigningKey");
}

export async function sendEmail(email: any) {
  return await api.put("/orgUnit/sendMessage?format=json", { data: email });
}

export async function getEvent(id: string) {
  return await api.get("/apiV1/getEvent/" + id).then(res => res.data);
}

export async function createTag(reqData: object) {
  try {
    return api.post(`/rest-api/tag/create`, reqData);
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function deleteTagAssociation(tagData: any) {
  try {
    return api.delete(`/rest-api/tag/delete/association/${tagData.id}`, {
      params: {
        context: tagData.context
      },
      data: tagData.tagBody
    });
  } catch (err) {
    console.log(err);
    return null;
  }
}
export async function getStripeUrl(orgId: string) {
  return await api.post(
    `/rest-api/payments/create-customer-portal-session/${orgId}`
  );
}
export async function getAllOrgTags(orgId: string) {
  const response = await api.get(`/rest-api/tag/names/${orgId}`);
  return response.data;
}

export async function getAllRelevantTags(
  orgUnitId: string,
  context: string,
  registrationStatus = ""
) {
  const response = await api.get(
    `/rest-api/tag/names/${orgUnitId}/${context}?registrationStatus=${registrationStatus}`
  );
  return response.data;
}

export async function createTagAssociation(reqData: any) {
  try {
    return api.put(
      `/rest-api/tag/association/add/${reqData.id}`,
      reqData.tagBody,
      { params: { context: reqData.context } }
    );
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function getOrgListData(tab: string) {
  try {
    const res = await api.get(`/rest-api/report/org-list?show=${tab}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}
export async function getStatsData(tab: string) {
  try {
    const res = await api.get(`/rest-api/report/stats?show=${tab}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}
export async function getFeatureWalkthrough(name: string) {
  try {
    const res = await api.get(
      `/rest-api/users/feature-walkthrough?walkthroughId=${name}`
    );
    return res.data;
  } catch (e) {
    console.log(e);
    return null;
  }
}
export async function updateFeatureWalkthrough(
  name: string,
  isCompleted: boolean
) {
  try {
    const res = await api.post(
      `/rest-api/users/update-feature-walkthrough?walkthroughId=${name}&isCompleted=${isCompleted}`
    );
    return res;
  } catch (e) {
    console.log(e);
    return null;
  }
}
